import {
  lazy,
  Suspense,
  type ReactElement,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { GlobalSearch } from '@/features/search';
import { buildSidebarSections } from '@/layouts/sidebar/sidebarNavBuilders';
import { preloadRouteForPath } from '@/navigation/routes/routePreload';
import { useNotifications } from '@/providers/notificationHooks';
import { getFilterEmptyLabel } from '@/shared/copy/filters';
import {
  appearanceFeatureFlags,
  isFeatureFlagEnabled,
} from '@/shared/featureFlags/appearanceFeatureFlags';
import { getDevPlanOverrideSummary } from '@/shared/plans/devPlanOverrides';
import { layoutSizes } from '@/theme/layoutSizes';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { r } from '@/theme/tokens/radii';
import { Box, Inline } from '@/ui';
import { StatusBanner } from '@/ui/composites';
import { SidebarNav } from '@/ui/composites/SidebarNav';
import { AppShell } from '@/ui/layout/AppShell';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import { Button, PageShell } from '@/ui/primitives';
import { AppSidebarBrand } from './AppSidebarBrand';
import { TopBarContextControls } from './TopBarContextControls';
import type { AuthSession } from '@/auth/types';
import type { NavItem } from '@/components/sidebarTypes';
import type { FeatureFlagResolveResult } from '@/shared/data/featureFlags';
import type { PlanAlert, PlanBadge } from '@/shared/plans/planStatus';
import type {
  OrganisationDto,
  OrganisationEntitlementsDto,
  PlanCode,
  ProjectDto,
} from '@flowqa/shared';

const TopBarActions = lazy(async () => {
  const module = await import('@/components/TopBarActions');
  return { default: module.TopBarActions };
});

type AuthenticatedLayoutProps = {
  session: AuthSession | null;
  isAdmin: boolean;
  isSuperuser: boolean;
  navItems: NavItem[];
  sidebarFeatureFlags?: FeatureFlagResolveResult['flags'] | null;
  addOns?: OrganisationEntitlementsDto['addOns'] | null;
  hideSidebar?: boolean;
  showSidebarWhenSignedOut?: boolean;
  forceShowSignOut?: boolean;
  restrictTopBarToSignOut?: boolean;
  disableTopBarSignOut?: boolean;
  activePath: string;
  projects: Array<
    Pick<ProjectDto, 'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'> & {
      locked: boolean;
      lockReason: string | null;
    }
  >;
  allProjects: Array<
    Pick<ProjectDto, 'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'> & {
      locked: boolean;
      lockReason: string | null;
    }
  >;
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  canCreateProject?: boolean;
  planName?: string | null;
  planCode?: PlanCode | null;
  planAlerts?: PlanAlert[];
  planBadge?: PlanBadge | null;
  uploadBlockedReason?: string | null;
  lockProjectSelector?: boolean;
  onNavigate: (path: string) => void;
  onAddProject: () => void;
  onAddOrganisation: () => void;
  onOpenFeedback: () => void;
  onBeforeNavigate?: (href: string, label: string) => void;
  onSignOut: () => void;
  children?: ReactNode;
};

export const AuthenticatedLayout = ({
  session,
  isAdmin,
  isSuperuser,
  navItems,
  sidebarFeatureFlags,
  addOns = null,
  hideSidebar = false,
  showSidebarWhenSignedOut = false,
  forceShowSignOut = false,
  restrictTopBarToSignOut = false,
  disableTopBarSignOut = false,
  activePath,
  projects,
  allProjects,
  organisations,
  canCreateProject = true,
  planName = null,
  planCode = null,
  planAlerts = [],
  planBadge = null,
  uploadBlockedReason = null,
  lockProjectSelector = false,
  onNavigate,
  onAddProject,
  onAddOrganisation,
  onOpenFeedback,
  onBeforeNavigate,
  onSignOut,
  children,
}: AuthenticatedLayoutProps): ReactElement => {
  const layoutMode = useLayoutMode();
  const { selectedOrganisationId, setSelectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const [searchParams] = useSearchParams();
  const { notify } = useNotifications();
  const resolvedProjectOptions = useMemo(
    () => (projects.length > 0 ? projects : allProjects),
    [allProjects, projects],
  );
  const projectLabel = useMemo(() => {
    if (selectedProjectId) {
      const selectedProject = resolvedProjectOptions.find(
        (project) => project.id === selectedProjectId,
      );
      return selectedProject?.name ?? getFilterEmptyLabel('allProjects');
    }
    if (lockProjectSelector && resolvedProjectOptions.length === 1) {
      return resolvedProjectOptions[0]?.name ?? getFilterEmptyLabel('allProjects');
    }
    return getFilterEmptyLabel('allProjects');
  }, [lockProjectSelector, resolvedProjectOptions, selectedProjectId]);
  const selectedSidebarProject = useMemo(() => {
    if (selectedProjectId) {
      return resolvedProjectOptions.find((project) => project.id === selectedProjectId) ?? null;
    }
    if (lockProjectSelector && resolvedProjectOptions.length === 1) {
      return resolvedProjectOptions[0] ?? null;
    }
    return null;
  }, [lockProjectSelector, resolvedProjectOptions, selectedProjectId]);
  const selectedOrganisationName =
    selectedOrganisationId === null
      ? (session?.orgName ?? null)
      : (organisations.find((org) => org.id === selectedOrganisationId)?.name ??
        (session?.orgId === selectedOrganisationId ? (session.orgName ?? null) : null));
  const resolvedSidebarOrganisationId =
    selectedOrganisationId ?? (organisations.length === 1 ? (organisations[0]?.id ?? null) : null);
  const resolvedSidebarProjectId =
    selectedProjectId ??
    (lockProjectSelector && resolvedProjectOptions.length === 1
      ? (resolvedProjectOptions[0]?.id ?? null)
      : null);
  const showTopbarOrganisationName = activePath !== '/onboarding';
  const topbarOrganisationLabel = selectedOrganisationName ?? 'Workspace';
  const selectedSidebarProjectLocked = selectedSidebarProject?.locked === true;
  const planAlertStorageKey = useMemo(() => {
    const resolvedOrganisationKey = selectedOrganisationId ?? session?.orgId ?? 'global';
    const resolvedUserKey = session?.userId ?? 'anonymous';
    return `flowqa-plan-alerts:${resolvedUserKey}:${resolvedOrganisationKey}`;
  }, [selectedOrganisationId, session?.orgId, session?.userId]);
  const [dismissedPlanAlerts, setDismissedPlanAlerts] = useState<string[]>([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const sidebarCollapsedActive = sidebarCollapsed && layoutMode === layoutModeTokens.wide;
  const devPlanOverrideSummary = useMemo(
    () => getDevPlanOverrideSummary(searchParams),
    [searchParams],
  );

  const shouldRenderSidebar = !hideSidebar && (Boolean(session) || showSidebarWhenSignedOut);
  const sidebarContent = useMemo(
    () =>
      buildSidebarSections({
        navItems,
        activePath,
        onNavigate,
        preloadRoute: preloadRouteForPath,
        featureFlags: sidebarFeatureFlags,
        addOns,
        isAdmin,
        isSuperuser,
        selectedOrganisationId,
        selectedProjectId,
        resolvedOrganisationId: resolvedSidebarOrganisationId,
        resolvedProjectId: resolvedSidebarProjectId,
        setSelectedOrganisationId,
        setSelectedProjectId,
        notify,
        projectsOptions: resolvedProjectOptions,
        allProjectsOptions: allProjects,
        organisationsOptions: organisations,
        canCreateProject,
        lockProjectSelector,
        projectLabel,
        projectBadge:
          selectedSidebarProjectLocked || !selectedSidebarProject?.badgeEnabled
            ? null
            : {
                chars: selectedSidebarProject.badgeChars,
                color: selectedSidebarProject.badgeColor,
              },
        projectLocked: selectedSidebarProjectLocked,
        onAddProject,
        onAddOrganisation,
        onOpenFeedback,
      }),
    [
      activePath,
      canCreateProject,
      isAdmin,
      isSuperuser,
      navItems,
      notify,
      onAddOrganisation,
      onOpenFeedback,
      onAddProject,
      onNavigate,
      allProjects,
      addOns,
      organisations,
      planName,
      planCode,
      resolvedProjectOptions,
      resolvedSidebarOrganisationId,
      resolvedSidebarProjectId,
      selectedOrganisationId,
      selectedProjectId,
      setSelectedOrganisationId,
      setSelectedProjectId,
      lockProjectSelector,
      projectLabel,
      planBadge,
      selectedSidebarProject,
      selectedSidebarProjectLocked,
      session,
      sidebarFeatureFlags,
    ],
  );

  const sidebar = shouldRenderSidebar ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <AppSidebarBrand collapsed={sidebarCollapsedActive} />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          pl: sidebarCollapsedActive ? s('md') : s('lg'),
          pr: sidebarCollapsedActive ? s('sm') : s('md'),
          pt: s('lg'),
          pb: s('md'),
        }}
      >
        <SidebarNav
          sections={sidebarContent.sections}
          preFooterItems={sidebarContent.preFooterItems}
          footerItems={sidebarContent.footerItems}
          collapsed={sidebarCollapsedActive}
          onToggleCollapsed={() => {
            setSidebarCollapsed((current) => !current);
          }}
          onBeforeNavigate={onBeforeNavigate}
        />
      </Box>
    </Box>
  ) : null;
  const restrictTopBarNavigation = activePath === '/onboarding' || restrictTopBarToSignOut;
  const shouldShowFallbackSignOut = forceShowSignOut || !session || restrictTopBarToSignOut;
  const [showDeferredTopBarActions, setShowDeferredTopBarActions] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      const raw = window.localStorage.getItem(planAlertStorageKey);
      if (!raw) {
        setDismissedPlanAlerts([]);
        return;
      }
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) {
        setDismissedPlanAlerts([]);
        return;
      }
      setDismissedPlanAlerts(parsed.filter((item): item is string => typeof item === 'string'));
    } catch {
      setDismissedPlanAlerts([]);
    }
  }, [planAlertStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(planAlertStorageKey, JSON.stringify(dismissedPlanAlerts));
  }, [dismissedPlanAlerts, planAlertStorageKey]);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }
    if (searchParams.get('devResetPlanAlerts') !== '1') {
      return;
    }
    try {
      window.localStorage.removeItem(planAlertStorageKey);
      setDismissedPlanAlerts([]);
    } catch {
      // ignore storage failures
    }
  }, [planAlertStorageKey, searchParams]);

  useEffect(() => {
    if (restrictTopBarNavigation || shouldShowFallbackSignOut) {
      setShowDeferredTopBarActions(false);
      return;
    }
    if (typeof window === 'undefined') {
      setShowDeferredTopBarActions(true);
      return;
    }
    let rafId: number | null = null;
    let timeoutId: number | null = null;
    const scheduleLoad = (): void => {
      timeoutId = window.setTimeout(() => {
        setShowDeferredTopBarActions(true);
      }, 200);
    };
    if (typeof window.requestAnimationFrame === 'function') {
      rafId = window.requestAnimationFrame(scheduleLoad);
    } else {
      scheduleLoad();
    }
    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [restrictTopBarNavigation, shouldShowFallbackSignOut]);
  const visiblePlanAlerts = useMemo(
    () => planAlerts.filter((alert) => !dismissedPlanAlerts.includes(alert.key)),
    [dismissedPlanAlerts, planAlerts],
  );
  const hasTopStatusBanners =
    devPlanOverrideSummary.length > 0 ||
    visiblePlanAlerts.length > 0 ||
    (uploadBlockedReason !== null && selectedSidebarProjectLocked);
  const topBarActionsFallback = (
    <Inline gap="sm" align="center" sx={{ width: 'auto', pr: s('sm') }}>
      <Box
        sx={{
          width: `calc(${s('2xl')} + ${s('md')})`,
          height: `calc(${s('2xl')} + ${s('md')})`,
          borderRadius: r('pill'),
        }}
      />
    </Inline>
  );
  const topBarActions =
    restrictTopBarNavigation || shouldShowFallbackSignOut ? (
      <Inline gap="md" align="center" sx={{ width: 'auto' }}>
        {disableTopBarSignOut ? null : (
          <Button variant="text" onClick={onSignOut}>
            Sign Out
          </Button>
        )}
      </Inline>
    ) : showDeferredTopBarActions ? (
      <Suspense fallback={topBarActionsFallback}>
        <TopBarActions session={session} actionsGap="lg" />
      </Suspense>
    ) : (
      topBarActionsFallback
    );
  const globalSearchEnabled = isFeatureFlagEnabled(
    sidebarFeatureFlags,
    appearanceFeatureFlags.globalSearch,
  );
  const topBarSearch =
    globalSearchEnabled &&
    session &&
    !restrictTopBarNavigation &&
    layoutMode === layoutModeTokens.wide ? (
      <GlobalSearch
        userId={session.userId}
        isSuperuser={isSuperuser}
        featureFlags={sidebarFeatureFlags}
      />
    ) : null;
  const actions = topBarSearch ? (
    <Inline gap="md" align="center" sx={{ width: 'auto', minWidth: 0 }}>
      {topBarSearch}
      {topBarActions}
    </Inline>
  ) : (
    topBarActions
  );

  const titleContent =
    session && !restrictTopBarNavigation && showTopbarOrganisationName ? (
      <TopBarContextControls
        activePath={activePath}
        organisationLabel={topbarOrganisationLabel}
        selectedOrganisationId={selectedOrganisationId}
        organisations={organisations}
        projectLabel={projectLabel}
        selectedProjectId={selectedProjectId}
        projects={resolvedProjectOptions}
        canCreateProject={canCreateProject}
        lockProjectSelector={lockProjectSelector}
        notify={notify}
        onNavigate={onNavigate}
        onAddProject={onAddProject}
        setSelectedOrganisationId={setSelectedOrganisationId}
        setSelectedProjectId={setSelectedProjectId}
      />
    ) : null;
  const content = (
    <Box
      key={activePath}
      sx={(theme) => ({
        width: '100%',
        maxWidth: layoutSizes.content.maxWidth,
        mx: 'auto',
        minWidth: 0,
        minHeight: '100%',
        backgroundColor: resolveSemanticColor(theme, 'pageBg'),
      })}
    >
      {hasTopStatusBanners ? (
        <Box sx={{ pt: s('xl') }}>
          <PageShell
            maxWidth="xl"
            paddingX="lg"
            paddingY="none"
            paddingTop="none"
            paddingBottom="none"
          >
            {devPlanOverrideSummary.length > 0 ? (
              <Box sx={{ mb: s('none') }}>
                <StatusBanner
                  tone="blue"
                  message={`Dev plan preview active: ${devPlanOverrideSummary.join(' · ')}`}
                  dense
                />
              </Box>
            ) : null}
            {visiblePlanAlerts.length > 0 ? (
              <Box sx={{ mb: s('none') }}>
                {visiblePlanAlerts.map((alert, index) => (
                  <Box key={alert.key} sx={index === 0 ? undefined : { mt: s('sm') }}>
                    <StatusBanner
                      tone={alert.tone}
                      message={alert.message}
                      onClose={() => {
                        setDismissedPlanAlerts((current) =>
                          current.includes(alert.key) ? current : [...current, alert.key],
                        );
                      }}
                    />
                  </Box>
                ))}
              </Box>
            ) : null}
            {uploadBlockedReason && selectedSidebarProjectLocked ? (
              <Box sx={{ mb: s('none') }}>
                <StatusBanner tone="orange" message={uploadBlockedReason} />
              </Box>
            ) : null}
          </PageShell>
        </Box>
      ) : null}
      {children}
    </Box>
  );

  return (
    <AppShell
      title={titleContent}
      actions={actions}
      sidebar={sidebar}
      sidebarCollapsed={sidebarCollapsedActive}
      showDefaultActions={false}
    >
      {content}
    </AppShell>
  );
};
